/**
 * Session API bridge for registry-demo.
 * Provides a singleton Session API instance and React integration.
 */

import {
  createInMemorySessionApi,
  type SessionApi,
  type SessionId,
  type SessionCreateOptions,
  type SessionMetadata,
  type SessionOverrides,
} from "./sessionApi";
import type { Item, ToolConfig, Lens } from "../harmonizer/types";
import type { PrioritySnapshot } from "./priorityTruth";

/**
 * Singleton Session API instance.
 */
export const sessionApi: SessionApi = createInMemorySessionApi("system");

/**
 * Extended Session API with app-specific helpers.
 */
export interface AppSessionApi {
  // Base SessionApi methods
  createSession(initialItemsOrOptions?: Item[] | SessionCreateOptions): SessionId;
  getSession(sessionId: SessionId): any;
  getSessionMetadata(sessionId: SessionId): SessionMetadata | undefined;
  updateSessionMetadata(
    sessionId: SessionId,
    updates: Partial<SessionMetadata> | ((current: SessionMetadata) => SessionMetadata)
  ): void;
  addItem(sessionId: SessionId, item: Item): Promise<void>;
  runTool(
    sessionId: SessionId,
    toolName: string,
    toolConfig: ToolConfig,
    lens: Lens | null
  ): Promise<PrioritySnapshot>;
  getPriorityState(sessionId: SessionId): any;
  getAuditEvents(sessionId: SessionId): any;

  /**
   * Gets the current active session ID.
   */
  getActiveSessionId(): SessionId | null;

  /**
   * Sets the active session ID.
   */
  setActiveSessionId(sessionId: SessionId): void;

  /**
   * Creates a new session and sets it as active.
   */
  createAndSetActiveSession(initialItemsOrOptions?: Item[] | SessionCreateOptions): SessionId;

  /**
   * Updates items in the active session.
   */
  updateItems(items: Item[]): Promise<void>;

  /**
   * Updates the criteria set for the active session.
   */
  updateCriteriaSet(criteriaSet: any): void;

  /**
   * Updates the override portion of session metadata.
   */
  updateSessionOverrides(overrides: Partial<SessionOverrides>): void;

  /**
   * Sets the lens for the active session.
   */
  setLens(lens: Lens | null): void;

  /**
   * Runs a tool on the active session (convenience method).
   */
  runToolOnActive(
    toolName: string,
    options: {
      toolConfig?: ToolConfig;
      lens?: Lens | null;
    }
  ): Promise<PrioritySnapshot>;

  /**
   * Publishes a snapshot, making it the authoritative published prioritization artifact.
   */
  publishSnapshot(snapshotId: string, focusListId?: string): void;

  /**
   * Gets explainability views for a snapshot.
   */
  getExplainabilityViews(
    snapshotId: string,
    options?: any
  ): Promise<any>;
}

/**
 * Active session ID storage.
 */
let activeSessionId: SessionId | null = null;

/**
 * Creates an extended session API with app-specific helpers.
 */
export function createAppSessionApi(): AppSessionApi {
  return {
    ...sessionApi,

    getActiveSessionId(): SessionId | null {
      return activeSessionId;
    },

    setActiveSessionId(sessionId: SessionId): void {
      activeSessionId = sessionId;
    },

    createAndSetActiveSession(initialItemsOrOptions: Item[] | SessionCreateOptions = []): SessionId {
      const newSessionId = sessionApi.createSession(initialItemsOrOptions);
      activeSessionId = newSessionId;
      return newSessionId;
    },

    async updateItems(items: Item[]): Promise<void> {
      if (!activeSessionId) {
        throw new Error("No active session. Create a session first.");
      }
      const session = sessionApi.getSession(activeSessionId);
      if (session) {
        session.gather.items = [];
        for (const item of items) {
          await sessionApi.addItem(activeSessionId!, item);
        }
      }
    },

    updateCriteriaSet(criteriaSet: any): void {
      if (!activeSessionId) {
        throw new Error("No active session. Create a session first.");
      }
      const session = sessionApi.getSession(activeSessionId);
      if (session) {
        session.arrange.criteriaSet = criteriaSet;
      }
    },

    updateSessionOverrides(overrides: Partial<SessionOverrides>): void {
      if (!activeSessionId) {
        throw new Error("No active session. Create a session first.");
      }
      sessionApi.updateSessionMetadata(activeSessionId, (current) => ({
        ...current,
        overrides: {
          ...current.overrides,
          ...overrides,
        },
      }));
    },

    setLens(lens: Lens | null): void {
      if (!activeSessionId) {
        throw new Error("No active session. Create a session first.");
      }
      const session = sessionApi.getSession(activeSessionId);
      if (session) {
        session.arrange.lens = lens;
      }
    },

    runTool: sessionApi.runTool.bind(sessionApi),

    async runToolOnActive(
      toolName: string,
      options: {
        toolConfig?: ToolConfig;
        lens?: Lens | null;
      }
    ): Promise<PrioritySnapshot> {
      if (!activeSessionId) {
        throw new Error("No active session. Create a session first.");
      }

      const session = sessionApi.getSession(activeSessionId);
      if (!session) {
        throw new Error(`Session ${activeSessionId} not found`);
      }

      const toolConfig = options.toolConfig || {};
      const lens = options.lens ?? session.arrange.lens ?? null;

      return sessionApi.runTool(activeSessionId, toolName, toolConfig, lens);
    },

    publishSnapshot(snapshotId: string, focusListId?: string): void {
      if (!activeSessionId) {
        throw new Error("No active session. Create a session first.");
      }
      
      const state = sessionApi.getPriorityState(activeSessionId);
      if (state) {
        const snapshot = state.history.find((s: any) => s.snapshotId === snapshotId);
        if (snapshot) {
          console.log(`Publishing snapshot ${snapshotId}${focusListId ? ` to focus list ${focusListId}` : ''}`);
          // The actual publishing logic is handled by the component that calls this,
          // which updates the workspace items and focus lists directly
        } else {
          console.warn(`Snapshot ${snapshotId} not found in session ${activeSessionId}`);
        }
      }
    },

    async getExplainabilityViews(
      _snapshotId: string,
      _options?: any
    ): Promise<any> {
      return {
        executiveView: null,
        traceView: null,
      };
    },
  };
}

/**
 * Singleton app session API instance.
 */
export const appSessionApi = createAppSessionApi();

